import { useMultipleSelection, useSelect } from "downshift";
import styles from "./MultiSelect.module.scss";
import OptionalPortal from "../util/OptionalPortal";
import { autoUpdate, flip, offset, useFloating } from "@floating-ui/react";
import classNames from "classnames";
import { Check, ChevronDown, ChevronUp, Close } from "../Icon/Icon";
import InputWrapper from "../InputWrapper/InputWrapper";
import { forwardRef, useMemo } from "react";

type MultiSelectProps = {
  data: { value: string; label: string }[];
  onChange?: (values: string[]) => void;
  onBlur?: () => void;
  value?: string[];
  placeholder?: string;
  withinPortal?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getItemsFilter(selectedItems: MultiSelectProps["data"]) {
  return function itemsFilter(item: MultiSelectProps["data"][0]) {
    return selectedItems.indexOf(item) < 0;
  };
}

const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  function MultiSelect(
    { data, withinPortal, placeholder, value, onChange },
    ref,
  ) {
    const { refs, floatingStyles } = useFloating({
      whileElementsMounted: autoUpdate,
      middleware: [flip(), offset(4)],
    });

    const controlledSelectedItems = data.filter((item) =>
      value?.includes(item.value),
    );

    const {
      getSelectedItemProps,
      getDropdownProps,
      addSelectedItem,
      removeSelectedItem,
      setSelectedItems,
      selectedItems,
    } = useMultipleSelection({
      // initialSelectedItems,
      selectedItems: controlledSelectedItems,
      onSelectedItemsChange: ({ selectedItems }) => {
        onChange?.(selectedItems?.map((item) => item.value) ?? []);
      },
    });

    const items = useMemo(() => {
      return data;
      // return data.filter(getItemsFilter(selectedItems));
    }, [data]);

    const {
      isOpen,
      getToggleButtonProps,
      getMenuProps,
      highlightedIndex,
      getItemProps,
    } = useSelect({
      selectedItem: null,
      defaultHighlightedIndex: 0, // after selection, highlight the first item.
      items,
      stateReducer: (state, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        const { highlightedIndex } = state;

        switch (type) {
          case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
          case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: true,
              highlightedIndex,
            };
        }
        return changes;
      },
      onStateChange: ({ type, selectedItem: newSelectedItem }) => {
        switch (type) {
          case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
          case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            // I can't remember why this is here, but it causes selection to happen clicking outside the dropdown
            // case useSelect.stateChangeTypes.ToggleButtonBlur:
            if (newSelectedItem) {
              if (
                selectedItems.find(
                  (selectedItem) =>
                    selectedItem.value === newSelectedItem.value,
                )
              ) {
                setSelectedItems(
                  selectedItems.filter(
                    (selectedItem) =>
                      newSelectedItem.value !== selectedItem.value,
                  ),
                );
              } else {
                addSelectedItem(newSelectedItem);
              }
            }
            break;
          default:
            break;
        }
      },
    });

    return (
      <div className={styles.container} ref={ref}>
        <div className={styles.inputWrapper} ref={refs.setReference}>
          <InputWrapper
            rightSection={
              isOpen ? (
                <ChevronUp size="medium" />
              ) : (
                <ChevronDown size="medium" />
              )
            }
          >
            <div
              className={styles.trigger}
              aria-label="toggle menu"
              {...getToggleButtonProps(
                getDropdownProps({ preventKeyAction: isOpen }),
              )}
            >
              {selectedItems.length ? (
                selectedItems.map(
                  function renderSelectedItem(selectedItemForRender, index) {
                    return (
                      <span
                        className={styles.selectedItem}
                        key={`selected-item-${selectedItemForRender.value}`}
                        {...getSelectedItemProps({
                          selectedItem: selectedItemForRender,
                          index,
                        })}
                      >
                        <span>{selectedItemForRender.label}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedItem(selectedItemForRender);
                          }}
                        >
                          <Close size="xsmall" />
                        </button>
                      </span>
                    );
                  },
                )
              ) : (
                <span className={styles.inputPlaceholder}>{placeholder}</span>
              )}
            </div>
          </InputWrapper>
        </div>

        <OptionalPortal withinPortal={withinPortal}>
          <ul
            {...getMenuProps({ ref: refs.setFloating })}
            style={{
              ...floatingStyles,
              maxWidth: refs.reference.current?.getBoundingClientRect().width,
            }}
            className={classNames(styles.menu, {
              [styles.menuClosed]: !isOpen,
            })}
          >
            {isOpen &&
              items?.map((item, index) => (
                <li
                  className={classNames(styles.menuItem, {
                    [styles.menuItemSelected]: highlightedIndex === index,
                  })}
                  key={item.value}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  {item.label}
                  {selectedItems.find(
                    (selectedItem) => selectedItem?.value === item.value,
                  ) && <Check size="medium" className={styles.check} />}
                </li>
              ))}
          </ul>
        </OptionalPortal>
      </div>
    );
  },
);

export default MultiSelect;
